import {
  NotificationsActive as NotificationsActiveIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AuthContext } from "../../../../../contexts/AuthContext";
import {
  deleteProductComment,
  fetchComments,
  removeCommentReminder,
  setCommentReminder,
  storeAnalyticsSelector,
  upsertProductComment,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";

const REMINDER_OPTIONS = [
  { label: "1 Day", value: "1_day" },
  { label: "1 Week", value: "1_week" },
  { label: "2 Weeks", value: "2_weeks" },
];

const formatReminderDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Helper to get reminder date from comment (supports both reminder_time and reminder_date fields)
const getCommentReminderDate = (comment) => {
  return comment.reminder_time || comment.reminder_date || null;
};

export function CommentableHeader({
  colDef,
  // Optional node shown instead of colDef.headerName.
  label,
  asin,
  store,
  dbComments,
  start_date,
  end_date,
  marketplace,
}) {
  const { user } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [autoAnchorEl, setAutoAnchorEl] = useState(null);
  const [comments, setComments] = useState([]);
  const [autoComments, setAutoComments] = useState([]);
  const [isEditComment, setEditComment] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [hoveredCommentId, setHoveredCommentId] = useState(null);
  const [reminderMenuPosition, setReminderMenuPosition] = useState(null);
  const [reminderCommentId, setReminderCommentId] = useState(null);
  const [removeReminderDialogOpen, setRemoveReminderDialogOpen] =
    useState(false);
  const [removeReminderCommentId, setRemoveReminderCommentId] = useState(null);
  const dispatch = useDispatch();

  // Read comments directly from Redux to ensure we always have fresh data
  // (DataGrid headers may not re-render when dbComments prop changes)
  const { productCommentsData } = useSelector(storeAnalyticsSelector);

  useEffect(() => {
    // Load existing comment if available - prefer Redux data over prop
    const commentsSource = productCommentsData || dbComments;
    if (commentsSource && commentsSource[asin]) {
      const FILTERED_OUT_AGENTS = ["Agent Regina", "Agent Ross", "Agent Rita"];
      const allFound = commentsSource[asin].filter(
        (c) =>
          c.asin === asin && c.store === store && c.cell === colDef.headerName
      );
      setComments(
        allFound.filter(
          (c) =>
            c.comment_type !== "automatic" &&
            !FILTERED_OUT_AGENTS.includes(c.user)
        )
      );
      setAutoComments(allFound.filter((c) => c.comment_type === "automatic"));
    }
  }, [productCommentsData, dbComments, asin, store, colDef.headerName]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleRefresh = () => {
    dispatch(fetchComments({ store, start_date, end_date, marketplace }));
  };
  const handleClose = () => {
    setAnchorEl(null);
    setNewComment("");
    setEditComment(null);
  };
  const deleteComment = (store, comment) => {
    let updatedComments;
    if (comment.user === user.email) {
      if (comment.id) {
        trackEvent("comment_deleted", {
          asin,
          store,
          column: colDef.headerName,
        });
        dispatch(
          deleteProductComment(
            store,
            comment.id,
            start_date,
            end_date,
            marketplace
          )
        );
        updatedComments = comments.filter((c) => c.id !== comment.id);
        setComments(updatedComments);
      }
    }
  };
  const editComment = (comment) => {
    if (comment.user === user.email) {
      trackEvent("comment_edit_started", {
        asin,
        store,
        column: colDef.headerName,
      });
      setEditComment(comment.id);
      setNewComment(comment.text);
    }
  };
  const openReminderMenu = (event, comment) => {
    event.stopPropagation();
    event.preventDefault();
    // If reminder already set, open remove confirmation dialog
    if (getCommentReminderDate(comment)) {
      setRemoveReminderCommentId(comment.id);
      setRemoveReminderDialogOpen(true);
    } else {
      // Otherwise, open the reminder options menu
      setReminderMenuPosition({
        top: event.clientY,
        left: event.clientX,
      });
      setReminderCommentId(comment.id);
    }
  };
  const closeReminderMenu = () => {
    setReminderMenuPosition(null);
    setReminderCommentId(null);
  };
  const handleRemoveReminder = () => {
    trackEvent("comment_reminder_removed", {
      asin,
      store,
      column: colDef.headerName,
    });
    if (removeReminderCommentId) {
      const payload = {
        comment_id: removeReminderCommentId,
        user: user.email,
        store: store,
        marketplace: marketplace,
      };
      dispatch(removeCommentReminder(payload, store, start_date, end_date));
      // Update local state to remove reminder
      setComments(
        comments.map((c) =>
          c.id === removeReminderCommentId
            ? { ...c, reminder_time: null, reminder_date: null }
            : c
        )
      );
    }
    setRemoveReminderDialogOpen(false);
    setRemoveReminderCommentId(null);
  };
  const closeRemoveReminderDialog = () => {
    setRemoveReminderDialogOpen(false);
    setRemoveReminderCommentId(null);
  };
  const calculateReminderDate = (preset, baseDate) => {
    // Use the comment's date as base, or fall back to current date
    const date = baseDate ? new Date(baseDate) : new Date();
    switch (preset) {
      case "1_day":
        date.setDate(date.getDate() + 1);
        break;
      case "1_week":
        date.setDate(date.getDate() + 7);
        break;
      case "2_weeks":
        date.setDate(date.getDate() + 14);
        break;
      default:
        break;
    }
    return date.toISOString();
  };

  const handleSetReminder = (reminderValue) => {
    trackEvent("comment_reminder_set", {
      asin,
      store,
      column: colDef.headerName,
      reminder_preset: reminderValue,
    });
    if (reminderCommentId) {
      // Find the comment to get its creation date
      const comment = comments.find((c) => c.id === reminderCommentId);
      const commentDate = comment?.created_at || comment?.updated_at;
      const reminderDate = calculateReminderDate(reminderValue, commentDate);
      const payload = {
        comment_id: reminderCommentId,
        user: user.email,
        reminder_time: reminderDate,
        store: store,
        marketplace: marketplace,
      };
      dispatch(setCommentReminder(payload, store, start_date, end_date));
      // Update local state to show reminder is set
      setComments(
        comments.map((c) =>
          c.id === reminderCommentId ? { ...c, reminder_time: reminderDate } : c
        )
      );
    }
    closeReminderMenu();
  };
  const saveComment = (cellDay, asin, store) => {
    trackEvent("comment_saved", {
      asin,
      store,
      column: cellDay,
      is_edit: isEditComment !== null,
    });
    let nowDate = new Date().toLocaleString("sv-SE");
    let commentObj;
    let updatedComments;
    if (isEditComment !== null) {
      // Edit existing comment
      updatedComments = comments.map((comment) => {
        if (comment.id === isEditComment) {
          commentObj = { ...comment, text: newComment, updated_at: nowDate };
          return commentObj;
        }
        return comment;
      });
    } else {
      commentObj = {
        store: store,
        asin: asin,
        cell: cellDay,
        text: newComment,
        user: user.email,
        created_at: nowDate,
        updated_at: nowDate,
      };
      updatedComments = [...comments, commentObj];
    }

    dispatch(
      upsertProductComment(commentObj, store, start_date, end_date, marketplace)
    );
    setComments(updatedComments);
    setNewComment("");
    setEditComment(null);
  };

  return (
    <Box display="flex" alignItems="center" style={{ cursor: "pointer" }}>
      <Box onClick={handleOpen}>{label ?? colDef.headerName}</Box>

      {/* Icons: 💬 then ✅ to its right, container anchored at right: 5 */}
      {(comments.length > 0 || newComment || autoComments.length > 0) && (
        <Box
          style={{
            position: "absolute",
            top: -5,
            right: 5,
            display: "flex",
            gap: "2px",
            fontSize: 10,
          }}
        >
          {(comments.length > 0 || newComment) && (
            <Box onClick={handleOpen} style={{ cursor: "pointer" }}>
              {"💬"}
            </Box>
          )}
          {autoComments.length > 0 && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setAutoAnchorEl(e.currentTarget);
              }}
              style={{ cursor: "pointer" }}
            >
              {"✅"}
            </Box>
          )}
        </Box>
      )}

      {/* Read-only popover for automatic comments */}
      <Popover
        open={Boolean(autoAnchorEl)}
        anchorEl={autoAnchorEl}
        onClose={() => setAutoAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        style={{ marginLeft: -10 }}
      >
        <Box p={2} style={{ minWidth: 200, border: "1px solid lightgray" }}>
          {autoComments.map((comment) => (
            <div
              key={comment.id}
              style={{
                borderBottom: "1px solid lightgray",
                paddingBottom: "10px",
                marginBottom: "10px",
              }}
            >
              <span
                style={{ marginLeft: 5, color: "#2e7d32", fontWeight: 500 }}
              >
                {comment.text}
              </span>
              <br />
              <span
                style={{
                  marginLeft: 5,
                  color: "gray",
                  fontSize: 10,
                  fontStyle: "italic",
                }}
              >
                {comment.updated_at?.replace("T", " ")}
              </span>
            </div>
          ))}
        </Box>
      </Popover>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        style={{ marginLeft: -10 }}
      >
        <Box p={2} style={{ minWidth: 200, border: "1px solid lightgray" }}>
          {comments.length > 0 &&
            !isEditComment &&
            comments.map((comment) => (
              <div
                style={{
                  borderBottom: "1px solid lightgray",
                  paddingBottom: "10px",
                  marginBottom: "10px",
                }}
                onMouseEnter={() => setHoveredCommentId(comment.id)}
                onMouseLeave={() => setHoveredCommentId(null)}
              >
                <span style={{ marginLeft: 5 }}> {comment.text} </span>
                <br />
                <span
                  style={{
                    marginLeft: 5,
                    color: "gray",
                    fontSize: 10,
                    fontStyle: "italic",
                  }}
                >
                  {(
                    comment.user.charAt(0).toUpperCase() + comment.user.slice(1)
                  ).replace("@atomic-growth.com", ", ")}
                </span>
                <span
                  style={{
                    color: "gray",
                    fontSize: 10,
                    fontStyle: "italic",
                  }}
                >
                  {comment.updated_at.replace("T", " ")}
                </span>
                {comment.user === user.email &&
                  hoveredCommentId === comment.id && (
                    <div
                      style={{
                        display: "inline-block",
                        alignContent: "flex-end",
                        maxHeigh: 10,
                      }}
                    >
                      <Button
                        onClick={() => editComment(comment)}
                        style={{ maxHeight: 10 }}
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => deleteComment(store, comment)}
                        style={{ maxHeight: 10 }}
                      >
                        Delete
                      </Button>
                      <Tooltip
                        title={
                          getCommentReminderDate(comment)
                            ? `Reminder: ${formatReminderDate(
                                getCommentReminderDate(comment)
                              )}`
                            : "Set reminder"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => openReminderMenu(e, comment)}
                          style={{ padding: 2, marginLeft: 20 }}
                        >
                          {getCommentReminderDate(comment) ? (
                            <NotificationsActiveIcon
                              style={{ fontSize: 16, color: "#1976d2" }}
                            />
                          ) : (
                            <NotificationsIcon
                              style={{ fontSize: 16, color: "gray" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                {getCommentReminderDate(comment) &&
                  hoveredCommentId !== comment.id && (
                    <Tooltip
                      title={`Reminder: ${formatReminderDate(
                        getCommentReminderDate(comment)
                      )}`}
                    >
                      <NotificationsActiveIcon
                        style={{
                          fontSize: 14,
                          color: "#1976d2",
                          marginLeft: 5,
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  )}
              </div>
            ))}
          <div>
            <TextField
              label="Add Comment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              style={{ marginTop: 10 }}
              size="small"
              fullWidth
              multiline
            />
            <Button
              onClick={() => saveComment(colDef.headerName, asin, store)}
              style={{ marginTop: 0 }}
            >
              Add
            </Button>
            <Button onClick={handleClose} style={{ marginTop: 0 }}>
              Close
            </Button>
            <Button onClick={handleRefresh} style={{ marginTop: 0 }}>
              Refresh
            </Button>
          </div>
          <Menu
            open={Boolean(reminderMenuPosition)}
            onClose={closeReminderMenu}
            anchorReference="anchorPosition"
            anchorPosition={reminderMenuPosition}
          >
            {REMINDER_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleSetReminder(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
          <Dialog
            open={removeReminderDialogOpen}
            onClose={closeRemoveReminderDialog}
          >
            <DialogTitle>Remove Reminder</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove the reminder for this comment?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRemoveReminderDialog}>Cancel</Button>
              <Button onClick={handleRemoveReminder} color="primary" autoFocus>
                Yes, Remove
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Popover>
    </Box>
  );
}
